/**
 * Authentication utility functions for AutoMusic
 */

export interface User {
  id: number;
  spotify_id: string;
  display_name: string;
  email: string;
  profile_image?: string;
  is_token_valid: boolean;
}

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'user_data';

/**
 * Get the authentication token from localStorage
 */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Set the authentication token in localStorage
 */
export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Remove the authentication token from localStorage
 */
export function removeToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

/**
 * Check if the user is authenticated
 */
export function isAuthenticated(): boolean {
  return !!getToken();
}

/**
 * Get user data from localStorage
 */
export function getUserData(): User | null {
  const userData = localStorage.getItem(USER_KEY);
  if (!userData) return null;

  try {
    return JSON.parse(userData);
  } catch {
    return null;
  }
}

/**
 * Set user data in localStorage
 */
export function setUserData(user: User): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

/**
 * Fetch current user from API
 */
export async function fetchCurrentUser(): Promise<User> {
  const apiUrl = import.meta.env.VITE_API_URL || '';
  const token = getToken();

  if (!token) {
    throw new Error('No authentication token');
  }

  const response = await fetch(`${apiUrl}/api/auth/me`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      removeToken();
      throw new Error('Authentication expired');
    }
    throw new Error('Failed to fetch user');
  }

  const user = await response.json();
  setUserData(user);
  return user;
}

/**
 * Initiate login flow - redirects to Spotify OAuth
 */
export async function initiateLogin(): Promise<void> {
  const apiUrl = import.meta.env.VITE_API_URL || '';
  window.location.href = `${apiUrl}/api/auth/login`;
}

/**
 * Logout - clear local storage and redirect to login
 */
export async function logout(): Promise<void> {
  removeToken();
  window.location.href = '/automusic/login';
}

/**
 * Make an authenticated API request
 */
export async function authenticatedFetch(
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> {
  const apiUrl = import.meta.env.VITE_API_URL || '';
  const token = getToken();

  if (!token) {
    throw new Error('No authentication token');
  }

  const response = await fetch(`${apiUrl}${endpoint}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (response.status === 401) {
    removeToken();
    window.location.href = '/automusic/login';
    throw new Error('Authentication expired');
  }

  return response;
}
